// filepath: /src/sections/FeaturesSection.js
import React, { useEffect, useState } from 'react';
import './FeaturesSection.css';

const FeaturesSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { 
        threshold: 0.1, // Reducir threshold para móviles
        rootMargin: '50px' // Agregar margen para activar antes
      }
    );

    const section = document.getElementById('features');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const features = [
    { 
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M16 2C8.3 2 2 8.3 2 16s6.3 14 14 14 14-6.3 14-14S23.7 2 16 2z" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M11 16l3 3 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Asistencia Médica con IA', 
      subtitle: 'Diagnóstico inteligente',
      description: 'Nuestro sistema de IA asiste al médico en tiempo real durante la consulta, sugiriendo diagnósticos diferenciales, identificando patrones y minimizando riesgos médicos basándose en el historial clínico completo.' 
    },
    { 
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M4 6h24v20H4V6z" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M8 12h16M8 16h12M8 20h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      title: 'Notas Médicas Automáticas', 
      subtitle: 'Documentación inteligente',
      description: 'El sistema escucha y transcribe la consulta en tiempo real, generando automáticamente notas médicas estructuradas, recetas digitales y reportes mientras el médico se enfoca en el paciente.' 
    },
    { 
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M12 2a3 3 0 0 0-3 3v1H7a1 1 0 0 0-1 1v22a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-2V5a3 3 0 0 0-3-3H12z" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="16" cy="16" r="3" stroke="currentColor" strokeWidth="2" fill="none"/>
        </svg>
      ),
      title: 'Gestión de Medicamentos', 
      subtitle: 'Análisis farmacológico',
      description: 'La IA analiza interacciones medicamentosas, ajusta dosis según el perfil del paciente y genera recetas optimizadas. Incluye alertas de alergias y seguimiento de adherencia al tratamiento.' 
    },
    { 
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M16 6v20M6 16h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="2" fill="none"/>
        </svg>
      ),
      title: 'Diagnóstico con Asistencia de Voz', 
      subtitle: 'Revisión inteligente',
      description: 'Sistema de revisión previa del diagnóstico modificable mediante comandos de voz. La IA valida la coherencia clínica y sugiere ajustes antes de finalizar el diagnóstico.' 
    },
    { 
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M4 4h6v6H4V4zM22 4h6v6h-6V4zM4 22h6v6H4v-6zM22 22h6v6h-6v-6z" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M14 8h4v4h-4V8zM14 20h4v4h-4v-4z" stroke="currentColor" strokeWidth="2" fill="none"/>
        </svg>
      ),
      title: 'Historial Clínico Global', 
      subtitle: 'Acceso universal',
      description: 'Historial clínico completo disponible globalmente y localmente. Los datos del paciente están accesibles en cualquier momento y lugar, con sincronización automática entre dispositivos.' 
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M2 4h28v18H2V4z" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M8 28h16M16 22v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="10" cy="12" r="2" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="22" cy="12" r="2" stroke="currentColor" strokeWidth="2" fill="none"/>
        </svg>
      ),
      title: 'Videoconferencias Múltiples',
      subtitle: 'Consulta especializada',
      description: 'Sistema de videoconferencia integrado que permite conectar múltiples especialistas en tiempo real para interconsultas, diagnósticos complejos y seguimiento multidisciplinario.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <rect x="4" y="4" width="24" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M8 8h16v10H8V8z" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="12" cy="13" r="2" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M16 15l2-2 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Análisis de Imágenes Médicas',
      subtitle: 'Radiografías y ultrasonidos',
      description: 'Análisis avanzado de radiografías y ultrasonidos con tecnología phantom. El sistema evalúa tiempos de recuperación, estado actual y evolución de tratamientos mediante IA especializada.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M16 8v8l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Soporte 24/7',
      subtitle: 'Asistencia continua',
      description: 'Soporte técnico y médico disponible las 24 horas. Incluye asistencia para emergencias, consultas urgentes y resolución de problemas técnicos en tiempo real.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M16 2L4 8v10c0 7.5 5.2 14.3 12 16 6.8-1.7 12-8.5 12-16V8L16 2z" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M12 16l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Seguridad de Datos',
      subtitle: 'Protección total',
      description: 'Máxima seguridad para datos de pacientes y médicos con encriptación de extremo a extremo, cumplimiento HIPAA y protocolos de seguridad de grado militar para proteger información sensible.'
    },
    { 
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M16 16m-6 0a6 6 0 1 1 12 0a6 6 0 1 1 -12 0" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M24 8l4 4-4 4M8 8L4 12l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 24h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      title: 'Red Social Clínica',
      subtitle: 'Colaboración médica',
      description: 'ThinkHealth funciona como una red social médica donde los profesionales pueden compartir casos clínicos, colaborar en diagnósticos y compartir datos del paciente con autorización previa.'
    },
    {
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" strokeWidth="2" fill="none"/>
        </svg>
      ),
      title: 'Colaboración Grehta.org', 
      subtitle: 'Especialistas en cardiología',
      description: 'Experiencia especializada en cardiología e hipertensión, potenciada por nuestra alianza estratégica con Grehta.org para ofrecer el mejor cuidado cardiovascular y formación médica continua.' 
    }
  ];

  return (
    <section id="features" className={`features-section ${isVisible ? 'visible' : ''}`}>
      <div className="features-background">
        <div className="features-orb orb-1"></div>
        <div className="features-orb orb-2"></div>
        <div className="features-orb orb-3"></div>
      </div>

      <div className="container">
        <div className="features-header">
          <div className={`section-badge ${isVisible ? 'animate-slide-up' : ''}`}>
            <span className="badge-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1l1.5 3h3.5l-2.5 2 1 3L8 7l-3 2 1-3L3.5 4h3.5L8 1z" fill="currentColor"/>
              </svg>
            </span>
            Sistema ThinkHealth
          </div>
          <h2 className={`section-title ${isVisible ? 'animate-slide-up' : ''}`}>
            <span className="gradient-text">Inteligencia Artificial</span> que 
            Revoluciona la Medicina
          </h2>
          <p className={`section-subtitle ${isVisible ? 'animate-slide-up' : ''}`}>
            Descubre el ecosistema médico más avanzado: desde asistencia con IA en tiempo real 
            hasta análisis de imágenes médicas, gestión integral de medicamentos y una red social 
            clínica que conecta profesionales de la salud globalmente.
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className={`feature-card glass-card ${isVisible ? 'animate-slide-up' : ''}`}
              style={{ animationDelay: `${0.1 + index * 0.1}s` }}
            >
              <div className="feature-icon">
                {feature.icon}
              </div>
              <div className="feature-content">
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-subtitle">{feature.subtitle}</p>
                <p className="feature-description">{feature.description}</p>
              </div>
              <div className="feature-hover-effect"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
